import type { FormEvent } from "react";
import { FormProvider } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import { Box, Grid } from "@mui/material";

import { Button, Form } from "@covertree/ui";
import type { OptionType } from "@covertree/types";
import { AdditionalInterestType } from "@covertree/new-business-graphql";

import {
  AdditionalInterestAddressFields,
  AdditionalInterestIdentityFields,
} from "./fields";
import type { AdditionalInterestFormMode } from "./formData";
import type { AdditionalInterestFormValues } from "./lenderAutofill";

const SUBMIT_LABEL: Record<AdditionalInterestFormMode, string> = {
  add: "Add",
  edit: "Update",
  replace: "Replace",
};

interface Props {
  readonly form: UseFormReturn<AdditionalInterestFormValues>;
  readonly mode: AdditionalInterestFormMode;
  readonly typeOptions: readonly OptionType<AdditionalInterestType>[];
  readonly canSubmit: boolean;
  readonly onSubmit: (event: FormEvent) => void;
  readonly onCancel: () => void;
  readonly isTenantPolicy?: boolean;
  readonly isSubmitting?: boolean;
  readonly submitLabel?: string;
  readonly fontSize?: number;
  readonly labelFontSize?: number;
}

export const AdditionalInterestFormBody = ({
  form,
  mode,
  typeOptions,
  canSubmit,
  onSubmit,
  onCancel,
  isTenantPolicy,
  isSubmitting = false,
  submitLabel,
  fontSize = 18,
  labelFontSize = 16,
}: Props) => (
  <FormProvider {...form}>
    <Form
      onSubmit={(event) => {
        // prevents triggering payment-form
        event.stopPropagation();
        onSubmit(event);
      }}>
      <Grid container spacing={2}>
        <AdditionalInterestIdentityFields
          control={form.control}
          typeOptions={typeOptions}
          isTenantPolicy={isTenantPolicy}
          // Replacing the billing party keeps the type it is replacing.
          typeDisabled={mode === "replace"}
          disabled={isSubmitting}
          fontSize={fontSize}
          labelFontSize={labelFontSize}
        />
        <AdditionalInterestAddressFields
          control={form.control}
          disabled={isSubmitting}
          fontSize={fontSize}
          labelFontSize={labelFontSize}
        />
      </Grid>
      <Box mt={4} columnGap={3} display="flex" justifyContent="flex-end">
        <Button type="submit" disabled={!canSubmit} loading={isSubmitting}>
          {submitLabel ?? SUBMIT_LABEL[mode]}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          disabled={isSubmitting}>
          Cancel
        </Button>
      </Box>
    </Form>
  </FormProvider>
);
